import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';

import { CalendarDays, Plus, X, Scissors, Pencil, Trash2, MoreVertical, Calendar, ChevronLeft, ChevronRight, LayoutGrid, Columns, AlignJustify, ChevronDown, History } from 'lucide-react';
import { appointments, subscribe, addAppointment, updateAppointment, deleteAppointment, catalogServices, users } from '../../data/store';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import { CustomSelect } from '../../components/CustomSelect';

const TIME_SLOTS = (() => {
  const slots = ['Walk-in'];
  for (let h = 8; h <= 21; h++) {
    ['00', '30'].forEach(m => {
      if (h === 21 && m === '30') return;
      const hour = h % 12 === 0 ? 12 : h % 12;
      const ampm = h < 12 ? 'AM' : 'PM';
      slots.push(`${hour}:${m} ${ampm}`);
    });
  }
  return slots;
})();

const STATUS_COLORS = {
  'requested': '#8b5cf6',
  'pending': '#f59e0b',
  'confirmed': '#3b82f6',
  'checked-in': '#06b6d4',
  'started': '#ec4899',
  'completed': '#10b981',
  'invoiced': '#64748b',
  'cancelled': '#ef4444',
  'no-show': '#a8a29e',
  'rescheduled': '#f97316',
  'expired': '#78716c'
};

const FILTER_STATUSES = [
  { label: 'All', value: 'All' },
  { label: 'Booked', value: 'Booked' },
  { label: 'In Service', value: 'In Service' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Missed Out', value: 'Missed Out' },
  { label: 'Cancelled', value: 'Cancelled' }
];

const STATUS_GROUPS = {
  'Booked': ['pending', 'confirmed', 'rescheduled'],
  'In Service': ['checked-in', 'started'],
  'Completed': ['completed', 'invoiced'],
  'Missed Out': ['no-show', 'expired'],
  'Cancelled': ['cancelled']
};

export const AppointmentsTab = ({ org, showNotification, onBillAppointment }) => {
  const [aptList, setAptList] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('apt.statusFilter') || 'All');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedApt, setSelectedApt] = useState(null);
  const [activeDate, setActiveDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [calendarViewDate, setCalendarViewDate] = useState(null);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [historyPage, setHistoryPage] = useState(1);
  const [calendarTitle, setCalendarTitle] = useState('');
  const [calendarView, setCalendarView] = useState(() => sessionStorage.getItem('apt.calendarView') || 'timeGridWeek');
  
  const calendarContainerRef = useRef(null);
  useEffect(() => {
    if (statusFilter !== 'history' && calendarContainerRef.current) {
      const el = calendarContainerRef.current;
      el.classList.remove('calendar-slide-in');
      void el.offsetWidth; // trigger reflow
      el.classList.add('calendar-slide-in');
    }
  }, [statusFilter]);

  useEffect(() => {
    const handleDayClick = (e) => {
      const header = e.target.closest('.fc-list-day');
      if (header) {
        const dateStr = header.getAttribute('data-date');
        if (dateStr) {
          setExpandedDays(prev => {
            const next = new Set(prev);
            if (next.has(dateStr)) {
               next.delete(dateStr);
               header.classList.remove('is-expanded');
            } else {
               next.add(dateStr);
               header.classList.add('is-expanded');
            }
            return next;
          });
        }
      }
    };
    document.addEventListener('click', handleDayClick);
    return () => document.removeEventListener('click', handleDayClick);
  }, []);

  useEffect(() => { sessionStorage.setItem('apt.statusFilter', statusFilter); }, [statusFilter]);

  const [newBooking, setNewBooking] = useState(() => {
    const d = new Date();
    const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      customerName: '', phone: '', serviceId: '', staffId: '', date: localDate, time: '10:00 AM', 
      duration: 30, price: 0, discount: 0, advancePayment: 0, status: 'pending', paymentStatus: 'unpaid', notes: ''
    };
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const calendarRef = useRef(null);

  const [dataVersion, setDataVersion] = useState(0);

  const [showMoreModal, setShowMoreModal] = useState(false);
  const [moreModalDate, setMoreModalDate] = useState(null);

  useEffect(() => {
    const update = () => {
      setAptList(appointments.filter(a => a.orgId === org.id));
      setDataVersion(v => v + 1);
    };
    update();
    return subscribe(update);
  }, [org.id]);

  const myServices = useMemo(() => catalogServices.filter(s => s.orgId === org.id), [org.id, dataVersion]);
  const myStaff = useMemo(() => users.filter(u => u.orgId === org.id && u.isServiceProvider), [org.id, dataVersion]);

  const accent = org.theme?.primary || '#0a5132';
  const todayStr = new Date().toISOString().slice(0, 10);

  const handleSave = async () => {
    const newErrors = {};
    if (!newBooking.customerName.trim()) newErrors.customerName = 'Please enter customer name.';
    if (!newBooking.serviceId) newErrors.serviceId = 'Please select a service.';
    if (!newBooking.date) newErrors.date = 'Please select a date.';
    if (!newBooking.time) newErrors.time = 'Please select a time.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    setSaving(true);
    try {
      const payload = {
        orgId: org.id,
        customerName: newBooking.customerName.trim(),
        phone: newBooking.phone.trim(),
        serviceId: newBooking.serviceId,
        staffId: newBooking.staffId,
        date: newBooking.date,
        time: newBooking.time,
        duration: Number(newBooking.duration) || 30,
        price: Number(newBooking.price) || 0,
        discount: Number(newBooking.discount) || 0,
        advancePayment: Number(newBooking.advancePayment) || 0,
        status: newBooking.status || 'pending',
        paymentStatus: newBooking.paymentStatus || 'unpaid',
        notes: newBooking.notes.trim()
      };

      if (editMode && selectedApt) {
        await updateAppointment(selectedApt.id, payload);
        if (showNotification) showNotification('Appointment updated successfully', 'success');
      } else {
        await addAppointment(payload);
        if (showNotification) showNotification('Appointment booked successfully', 'success');
      }
      
      setShowModal(false);
      setEditMode(false);
      if (editMode) setSelectedApt(null);
      setNewBooking({ 
        customerName: '', phone: '', serviceId: '', staffId: '', date: todayStr, time: '10:00 AM', 
        duration: 30, price: 0, discount: 0, advancePayment: 0, status: 'pending', paymentStatus: 'unpaid', notes: '' 
      });
    } catch (err) {
      if (showNotification) showNotification('Failed to book appointment.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEventClick = (info) => {
    const aptId = info.event.id;
    const apt = aptList.find(a => a.id === aptId);
    if (apt) {
      setSelectedApt(apt);
    }
  };

  const handleDateSelect = (selectInfo) => {
    const dateStr = selectInfo.startStr.slice(0, 10);
    let timeStr = '10:00 AM';
    if (selectInfo.start.getHours() || selectInfo.start.getMinutes()) {
      const h = selectInfo.start.getHours();
      const m = selectInfo.start.getMinutes();
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      timeStr = `${hour12}:${m === 0 ? '00' : '30'} ${ampm}`;
    }
    
    setNewBooking(prev => ({
      ...prev,
      date: dateStr,
      time: timeStr
    }));
    setShowModal(true);
  };

  const events = useMemo(() => {
    const filteredList = statusFilter === 'All' 
      ? aptList 
      : statusFilter === 'history'
        ? aptList.filter(apt => ['completed', 'cancelled', 'no-show', 'expired'].includes(apt.status))
        : aptList.filter(apt => {
            if (STATUS_GROUPS[statusFilter]) {
              return STATUS_GROUPS[statusFilter].includes(apt.status);
            }
            return apt.status === statusFilter;
          });

    return filteredList.map(apt => {
      const service = myServices.find(s => s.id === apt.serviceId);
      let h = new Date().getHours();
      let m = new Date().getMinutes();
      
      if (apt.time && apt.time !== 'Walk-in' && apt.time.includes(':')) {
        const [time, period] = apt.time.split(' ');
        const [hourStr, minStr] = time.split(':');
        h = Number(hourStr) || 0;
        m = Number(minStr) || 0;
        if (period === 'PM' && h !== 12) h += 12;
        if (period === 'AM' && h === 12) h = 0;
      }
      
      const startDate = new Date(apt.date);
      startDate.setHours(h, m, 0);
      
      const endDate = new Date(startDate);
      const duration = apt.duration || service?.duration || 30;
      endDate.setMinutes(endDate.getMinutes() + Number(duration));

      return {
        id: apt.id,
        title: `${apt.customerName} - ${service?.name || 'Service'}`,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        resourceId: apt.staffId,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        extendedProps: {
          status: apt.status,
          statusColor: STATUS_COLORS[apt.status] || accent,
          customerName: apt.customerName,
          serviceName: service?.name || 'Service'
        }
      };
    });
  }, [aptList, myServices, accent, statusFilter]);

  const calendarEvents = useMemo(() => {
    if (calendarView === 'dayGridMonth' || calendarView.includes('list')) {
      return events;
    }
    const groups = {};
    events.forEach(ev => {
      const timeKey = ev.start;
      if (!groups[timeKey]) groups[timeKey] = [];
      groups[timeKey].push(ev);
    });
    
    const clustered = [];
    Object.values(groups).forEach(group => {
      if (group.length === 1) {
        clustered.push(group[0]);
      } else {
        const rep = { ...group[0] };
        rep.extendedProps = {
          ...rep.extendedProps,
          clusterCount: group.length
        };
        clustered.push(rep);
      }
    });
    return clustered;
  }, [events, calendarView]);

  return (
    <div className="flex-1 flex flex-col h-full w-full bg-white" style={{ '--theme-primary': accent }}>
      <header className="pt-4 px-6 pb-3 bg-white shadow-sm border-b border-slate-200/60 z-10 relative">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-sm" style={{ background: accent }}>
              <CalendarDays size={18} strokeWidth={2.5} />
            </div>
            <div className="flex flex-col justify-center">
              <h2 className="text-lg font-bold text-slate-900 tracking-tight leading-none mb-1">Appointments</h2>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">Calendar View</span>
            </div>
          </div>
          
          <div className="hidden lg:flex items-center space-x-2 px-4">
            {FILTER_STATUSES.map(filter => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider transition-all whitespace-nowrap border-b-2 ${
                  statusFilter === filter.value
                    ? ''
                    : 'text-slate-400 border-transparent hover:text-slate-600 hover:border-slate-300'
                }`}
                style={statusFilter === filter.value ? { borderColor: accent, color: accent } : {}}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="flex lg:hidden items-center px-2">
            <CustomSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={FILTER_STATUSES}
              className="w-32 bg-white border border-slate-200 rounded text-[10px] font-bold uppercase tracking-wider text-slate-700 h-8 px-2"
            />
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setStatusFilter(statusFilter === 'history' ? 'All' : 'history')}
              title={statusFilter === 'history' ? 'Back to Calendar' : 'Overall Log'}
              className={`inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors shadow-sm ${
                statusFilter === 'history' 
                  ? 'text-white hover:opacity-90' 
                  : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
              style={statusFilter === 'history' ? { background: accent } : {}}
            >
              {statusFilter === 'history' ? <Calendar size={16} strokeWidth={2.5} /> : <History size={16} strokeWidth={2.5} />}
            </button>
            <button
              onClick={() => {
                setEditMode(false);
                setNewBooking({ 
                  customerName: '', phone: '', serviceId: '', staffId: '', date: todayStr, time: '10:00 AM', 
                  duration: 30, price: 0, discount: 0, advancePayment: 0, status: 'pending', paymentStatus: 'unpaid', notes: '' 
                });
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 h-9 text-white rounded-lg text-xs font-bold transition-colors shadow-sm ml-2"
              style={{ background: accent }}
            >
              <Plus size={16} strokeWidth={3} /> New Booking
            </button>
          </div>
        </div>
      </header>


      <div className={`flex-1 min-h-0 pb-6 bg-white overflow-hidden flex flex-col ${statusFilter === 'history' ? '' : 'px-6 pt-3'}`}>
        <style dangerouslySetInnerHTML={{__html: `
          .fc { font-family: 'Inter', system-ui, sans-serif; font-size: 0.8125rem; }
          .fc-toolbar-title { font-size: 1rem !important; font-weight: 700 !important; color: #1e293b; letter-spacing: -0.01em; text-transform: uppercase !important; }
          .fc-row-hidden { display: none !important; }
            /* Base buttons - styled like Edit/Delete modal buttons */
            .fc .fc-button-primary { font-weight: 700 !important; font-size: 10px !important; text-transform: uppercase !important; letter-spacing: 0.1em !important; border-radius: 4px !important; padding: 0.25rem 0.5rem !important; border: 1px solid transparent !important; background: transparent !important; color: #64748b !important; box-shadow: none !important; transition: all 0.2s ease !important; display: inline-flex; align-items: center; justify-content: center; }
            .fc .fc-button-primary:hover { background: #ffffff !important; color: #1e293b !important; border-color: #cbd5e1 !important; box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important; }
            .fc .fc-button-primary:focus { box-shadow: none !important; outline: none !important; }

            /* The Segmented Control Track */
            .fc .fc-button-group { background: #f1f5f9 !important; border-radius: 6px !important; padding: 2px !important; gap: 1px !important; display: inline-flex !important; box-shadow: inset 0 1px 2px rgba(0,0,0,0.05) !important; border: 1px solid #e2e8f0 !important; }
            .fc .fc-button-group > .fc-button-primary { border: none !important; background: transparent !important; color: #64748b !important; border-radius: 4px !important; margin: 0 !important; box-shadow: none !important; padding: 0.2rem 0.6rem !important; transition: all 0.2s ease !important; font-size: 9px !important; }
            .fc .fc-button-group > .fc-button-primary:hover { color: #0f172a !important; }
            .fc .fc-button-group > .fc-button-primary.fc-button-active, .fc .fc-button-group > .fc-button-primary:not(:disabled):active { background: #ffffff !important; color: var(--theme-primary) !important; box-shadow: 0 1px 2px rgba(0,0,0,0.08), 0 1px 1px rgba(0,0,0,0.04) !important; }

            /* Left side navigation spacing */
            .fc-toolbar-chunk:first-child { display: flex; gap: 4px; align-items: center; }
            
            /* Today button */
            .fc-toolbar-chunk:first-child > .fc-button-primary:not(.fc-prev-button):not(.fc-next-button) { background: transparent !important; color: #64748b !important; border-radius: 4px !important; padding: 0.25rem 0.5rem !important; border: 1px solid transparent !important; box-shadow: none !important; }
            .fc-toolbar-chunk:first-child > .fc-button-primary:not(.fc-prev-button):not(.fc-next-button):hover { background: #ffffff !important; border-color: #cbd5e1 !important; color: #1e293b !important; box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important; }
            .fc-toolbar-chunk:first-child > .fc-button-primary:disabled { opacity: 0.5; cursor: not-allowed; background: transparent !important; color: #94a3b8 !important; border-color: transparent !important; box-shadow: none !important; }
            
            /* Adjust arrows padding and size (Match the transparent aesthetic) */
            .fc .fc-prev-button, .fc .fc-next-button { padding: 0.25rem 0.35rem !important; display: inline-flex !important; align-items: center !important; justify-content: center !important; border: 1px solid transparent !important; background: transparent !important; color: #64748b !important; border-radius: 4px !important; }
            .fc .fc-prev-button:hover, .fc .fc-next-button:hover { background: #ffffff !important; border-color: #cbd5e1 !important; color: #1e293b !important; box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important; }
            .fc .fc-prev-button .fc-icon, .fc .fc-next-button .fc-icon { font-size: 0.95rem !important; vertical-align: middle; }
          .fc-event { cursor: pointer; border-radius: 0 !important; border: none !important; transition: all 0.15s ease; overflow: hidden; background: transparent !important; }
          .fc-timegrid-event-harness > .fc-timegrid-event { right: 0 !important; margin-right: 0 !important; }
          .fc-v-event { border-radius: 0 !important; right: 0 !important; margin-right: 0 !important; }
          .fc-event-main { padding: 0 !important; height: 100%; width: 100%; }
          .fc-event:hover { transform: translateY(-1px); z-index: 50 !important; }
          .fc-timegrid-slot { height: 36px !important; }
          .fc-col-header-cell-cushion { padding: 10px 4px !important; font-weight: 700 !important; color: #475569; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.05em; }
          .fc-theme-standard th, .fc-theme-standard td { border-color: #f1f5f9 !important; }
          
          /* Custom List View Layout */
          .fc-list-event-time, .fc-list-event-graphic { display: none !important; }
          .fc-list-event-title { padding: 0 !important; border: none !important; }
          .fc-list-event { background: transparent !important; }
          .fc-list-event:hover td { background: transparent !important; }

          /* Right align +more link in month view (Bottom) */
          .fc-daygrid-day-bottom { display: flex; justify-content: flex-end; padding-right: 4px; padding-bottom: 2px; }
          .fc-daygrid-more-link { font-size: 10px !important; font-weight: 800 !important; text-transform: uppercase !important; color: var(--theme-primary) !important; pointer-events: none !important; }

          @keyframes calendarSlideIn {
            0% { opacity: 0.4; transform: translateX(-20px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          .calendar-slide-in {
            animation: calendarSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        `}} />
        <AnimatePresence mode="wait">
          <motion.div
            key={statusFilter === 'history' ? 'history' : 'calendar'}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2 }}
            className="w-full flex-1 min-h-0 flex flex-col h-full bg-white"
          >
            {statusFilter === 'history' ? (
              <div className="w-full h-full flex flex-col flex-1 bg-white">
                <div className="overflow-auto custom-scrollbar flex-1 bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                  <table className="w-full min-w-[800px] text-left border-collapse table-fixed bg-white">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                        <th className="w-[20%] px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">Date & Time</th>
                        <th className="w-[25%] px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">Customer</th>
                        <th className="w-[20%] px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">Staff</th>
                        <th className="w-[20%] px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">Service</th>
                        <th className="w-[15%] px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right whitespace-nowrap">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filteredHistory = aptList.filter(apt => ['completed', 'cancelled', 'no-show', 'expired'].includes(apt.status)).sort((a,b) => new Date(b.date) - new Date(a.date));
                        const startIndex = (historyPage - 1) * 10;
                        const paginatedHistory = filteredHistory.slice(startIndex, startIndex + 10);
                        const rows = paginatedHistory.map(apt => {
                          const service = myServices.find(s => s.id === apt.serviceId);
                          const staff = myStaff.find(s => s.id === apt.staffId);
                          const statusColor = STATUS_COLORS[apt.status] || accent;
                          return (
                            <tr key={apt.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors h-[60px]">
                              <td className="px-6 py-3">
                                <div className="font-bold text-slate-800 text-xs">{new Date(apt.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                                <div className="text-[10px] font-medium text-slate-500 mt-0.5">{apt.time || 'Walk-in'}</div>
                              </td>
                              <td className="px-6 py-3">
                                <div className="font-bold text-slate-800 text-xs">{apt.customerName}</div>
                                {apt.phone && <div className="text-[10px] font-medium text-slate-500 mt-0.5">{apt.phone}</div>}
                              </td>
                              <td className="px-6 py-3 text-xs font-semibold text-slate-700">{staff?.name || 'Unassigned'}</td>
                              <td className="px-6 py-3 text-xs font-semibold text-slate-700">{service?.name || 'Unknown'}</td>
                              <td className="px-6 py-3 text-right">
                                <span className="text-[10px] font-extrabold uppercase tracking-widest block" style={{ color: statusColor }}>
                                  {apt.status.replace('-', ' ')}
                                </span>
                              </td>
                            </tr>
                          );
                        });
                        return rows;
                      })()}
                    </tbody>
                  </table>
                  {aptList.filter(apt => ['completed', 'cancelled', 'no-show', 'expired'].includes(apt.status)).length === 0 && (
                    <div className="p-12 text-center text-slate-400 font-medium text-sm">No historical appointments found.</div>
                  )}
                </div>
                {(() => {
                  const totalHistory = aptList.filter(apt => ['completed', 'cancelled', 'no-show', 'expired'].includes(apt.status)).length;
                  const totalPages = Math.max(1, Math.ceil(totalHistory / 10));
                  if (totalPages <= 1) return null;
                  return (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50/80">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Page {historyPage} of {totalPages}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setHistoryPage(p => Math.max(1, p - 1))} disabled={historyPage === 1} className="px-4 py-2 rounded bg-white border border-slate-300 text-[10px] font-extrabold text-slate-700 disabled:opacity-40 disabled:bg-slate-50 hover:bg-slate-50 hover:border-slate-400 uppercase tracking-widest transition-colors shadow-sm">Prev</button>
                        <button onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))} disabled={historyPage === totalPages} className="px-4 py-2 rounded bg-white border border-slate-300 text-[10px] font-extrabold text-slate-700 disabled:opacity-40 disabled:bg-slate-50 hover:bg-slate-50 hover:border-slate-400 uppercase tracking-widest transition-colors shadow-sm">Next</button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
            <div ref={calendarContainerRef} className="w-full flex flex-col h-full calendar-slide-in">
              <div className="flex flex-row items-center justify-between mb-2 w-full">
                <div className="flex items-center gap-2">
                  <button type="button" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all" onClick={() => calendarRef.current?.getApi().today()}>
                    <Calendar size={12} /> Today
                  </button>
                  <div className="flex items-center gap-0.5">
                    <button type="button" className="inline-flex items-center justify-center w-7 h-7 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm transition-all" onClick={() => calendarRef.current?.getApi().prev()}><ChevronLeft size={14} /></button>
                    <button type="button" className="inline-flex items-center justify-center w-7 h-7 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm transition-all" onClick={() => calendarRef.current?.getApi().next()}><ChevronRight size={14} /></button>
                  </div>
                </div>
                <CustomDatePickerFixed
                  value={activeDate}
                  viewDate={calendarViewDate}
                  events={aptList.map(apt => {
                    const service = myServices.find(s => s.id === apt.serviceId);
                    return {
                      date: apt.date ? apt.date.slice(0, 10) : '',
                      title: `${apt.customerName} - ${service?.name || 'Service'}`,
                      customerName: apt.customerName,
                      serviceName: service?.name || 'Service',
                      time: apt.time,
                      status: apt.status
                    };
                  })}
                  onChange={(newDate) => {
                    if (newDate) {
                      setActiveDate(newDate);
                      calendarRef.current?.getApi().gotoDate(newDate);
                    }
                  }}
                  customTrigger={
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors group">
                      <h2 className="text-xs font-extrabold uppercase tracking-widest text-slate-800 group-hover:text-[var(--theme-primary)] transition-colors">{calendarTitle}</h2>
                      <ChevronDown size={14} className="text-slate-400 group-hover:text-[var(--theme-primary)] transition-colors" />
                    </div>
                  }
                />
                <div className="flex items-center gap-1">
                  <button type="button" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-[0.1em] transition-all border ${calendarView === 'dayGridMonth' ? 'bg-white border-slate-300 text-[var(--theme-primary)] shadow-sm' : 'bg-transparent border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm'}`} onClick={() => calendarRef.current?.getApi().changeView('dayGridMonth')}>
                    <LayoutGrid size={12} /> Month
                  </button>
                  <button type="button" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-[0.1em] transition-all border ${calendarView === 'timeGridWeek' ? 'bg-white border-slate-300 text-[var(--theme-primary)] shadow-sm' : 'bg-transparent border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm'}`} onClick={() => calendarRef.current?.getApi().changeView('timeGridWeek')}>
                    <Columns size={12} /> Week
                  </button>
                  <button type="button" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-[0.1em] transition-all border ${calendarView === 'timeGridDay' ? 'bg-white border-slate-300 text-[var(--theme-primary)] shadow-sm' : 'bg-transparent border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm'}`} onClick={() => calendarRef.current?.getApi().changeView('timeGridDay')}>
                    <AlignJustify size={12} /> Day
                  </button>
                </div>
              </div>
              <FullCalendar
                height="100%"
                ref={calendarRef}
                eventMinHeight={26}
                schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, resourceTimeGridPlugin]}
                initialView={calendarView}
                datesSet={(arg) => {
                  sessionStorage.setItem('apt.calendarView', arg.view.type);
                  setCalendarView(arg.view.type);
                  setCalendarTitle(arg.view.title);
                  
                  const currentDate = arg.view.calendar.getDate();
                  if (currentDate) {
                    const year = currentDate.getFullYear();
                    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                    const day = String(currentDate.getDate()).padStart(2, '0');
                    setCalendarViewDate(`${year}-${month}-${day}`);
                  }
                }}
                headerToolbar={false}
                views={{
                  resourceTimeGridDay: { buttonText: 'Staff' }
                }}
          resources={myStaff.map(staff => ({ id: staff.id, title: staff.name }))}
          slotMinTime="08:00:00"
          slotMaxTime="22:00:00"
          expandRows={true}
          allDaySlot={false}
          events={calendarEvents}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={1}
          eventMaxStack={1}
          moreLinkClick={(arg) => {
            return "none";
          }}
          eventClassNames={(arg) => {
            if (arg.view.type.includes('list')) {
              const date = arg.event.start;
              const yyyy = date.getFullYear();
              const mm = String(date.getMonth() + 1).padStart(2, '0');
              const dd = String(date.getDate()).padStart(2, '0');
              const dateStr = `${yyyy}-${mm}-${dd}`;
              if (!expandedDays.has(dateStr)) {
                return ['fc-row-hidden'];
              }
            }
            return [];
          }}
          editable={true}
          droppable={true}
          eventDurationEditable={true}
          nowIndicator={true}
          eventClick={(info) => {
            if (info.view.type === 'dayGridMonth' || info.event.extendedProps.clusterCount > 1) {
              const date = info.event.start;
              const y = date.getFullYear();
              const m = String(date.getMonth() + 1).padStart(2, '0');
              const d = String(date.getDate()).padStart(2, '0');
              setMoreModalDate(`${y}-${m}-${d}`);
              setShowMoreModal(true);
            } else {
              handleEventClick(info);
            }
          }}
          select={handleDateSelect}
          eventDrop={async (info) => {
            const aptId = info.event.id;
            const apt = aptList.find(a => a.id === aptId);
            if (apt) {
              const newStart = info.event.start;
              const dateStr = newStart.toISOString().slice(0, 10);
              const h = newStart.getHours();
              const m = newStart.getMinutes();
              const ampm = h >= 12 ? 'PM' : 'AM';
              const hour12 = h % 12 || 12;
              const timeStr = `${hour12}:${m === 0 ? '00' : '30'} ${ampm}`;
              try {
                await updateAppointment(apt.id, { ...apt, date: dateStr, time: timeStr, status: 'rescheduled' });
                if (showNotification) showNotification('Appointment rescheduled!', 'success');
              } catch (err) {
                if (showNotification) showNotification('Failed to reschedule.', 'error');
                info.revert();
              }
            }
          }}
          eventResize={async (info) => {
            if (showNotification) showNotification('Time updated!', 'success');
          }}
          eventContent={(eventInfo) => {
            const { statusColor, customerName, serviceName, status } = eventInfo.event.extendedProps;
            
            // Render a premium mini-card for the Month view
            if (eventInfo.view.type === 'dayGridMonth') {
              const start = eventInfo.event.start;
              const end = eventInfo.event.end;
              const startTimeStr = start ? start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase() : '';
              const endTimeStr = end ? end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase() : '';
              const timeDisplay = end ? `${startTimeStr} - ${endTimeStr}` : startTimeStr;

              return (
                <div 
                  className="flex flex-col gap-1 w-full overflow-hidden px-2.5 py-1.5 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_2px_5px_rgba(0,0,0,0.08)] transition-all cursor-pointer"
                  style={{ 
                    borderLeft: `2px solid ${statusColor}`, 
                    borderTop: '1px solid #f1f5f9',
                    borderRight: '1px solid #f1f5f9',
                    borderBottom: '1px solid #f1f5f9',
                    borderRadius: '0px' 
                  }}
                >
                  <div className="flex justify-between items-start w-full gap-1.5">
                    <span className="font-bold text-slate-900 text-[11px] leading-tight truncate">
                      {customerName}
                    </span>
                    <span className="text-[10px] font-bold uppercase leading-none shrink-0" style={{ color: statusColor }}>
                      {status}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium text-[10px] text-slate-600 truncate leading-tight">
                      {serviceName}
                    </span>
                    <span className="font-semibold text-[10px] text-slate-500 truncate leading-tight mt-0.5">
                      {timeDisplay}
                    </span>
                  </div>
                </div>
              );
            }

            // Render a clean grid row for the List view
            if (eventInfo.view.type === 'listWeek' || eventInfo.view.type === 'listDay') {
              const start = eventInfo.event.start;
              const end = eventInfo.event.end;
              const startTimeStr = start ? start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase() : '';
              const endTimeStr = end ? end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase() : '';
              const timeDisplay = end ? `${startTimeStr} - ${endTimeStr}` : startTimeStr;

              return (
                <div className="grid grid-cols-4 gap-y-1.5 gap-x-6 w-full py-3.5 px-6 bg-white hover:bg-slate-50 transition-colors border-l-4 border-b border-slate-100 items-center" style={{ borderLeftColor: statusColor }}>
                  {/* Headers */}
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Customer Name</span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Timing</span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Service</span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Status</span>
                  
                  {/* Values */}
                  <span className="font-bold text-slate-800 text-[12px] truncate">{customerName}</span>
                  <span className="font-bold text-slate-800 text-[11px]">{timeDisplay || 'N/A'}</span>
                  <span className="font-medium text-slate-600 text-[12px] truncate">{serviceName}</span>
                  <div className="flex justify-end">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: statusColor }}>
                      {status}
                    </span>
                  </div>
                </div>
              );
            }

            const start = eventInfo.event.start;
            const end = eventInfo.event.end;
            const startTimeStr = start ? start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';
            const endTimeStr = end ? end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';
            const timeDisplay = end ? `${startTimeStr} - ${endTimeStr}` : startTimeStr;
            const durationMins = end && start ? (end.getTime() - start.getTime()) / 60000 : 30;
            const isCompact = durationMins <= 30;

            const clusterCount = eventInfo.event.extendedProps.clusterCount || 1;
            const extraCount = clusterCount > 1 ? clusterCount - 1 : 0;

            if (isCompact) {
              return (
                <div 
                  className="flex flex-col items-start justify-center w-full h-full px-1.5 py-0.5 overflow-hidden rounded-none relative"
                  style={{ backgroundColor: `${statusColor}25`, borderLeft: `3px solid ${statusColor}` }}
                  title={`${customerName} - ${serviceName} (${status}) ${extraCount > 0 ? `+ ${extraCount} more` : ''}`}
                >
                  <div className="flex items-center gap-1 w-full pr-8">
                    <span className="text-[10px] text-slate-800 font-bold leading-none shrink-0">{startTimeStr}</span>
                    <span className="font-semibold text-[10px] text-slate-700 leading-none whitespace-normal">{customerName}</span>
                  </div>
                  {extraCount > 0 && (
                    <span className="absolute top-0.5 right-1 shrink-0 text-[var(--theme-primary)] text-[10px] font-bold uppercase tracking-wider leading-none drop-shadow-sm" style={{ backgroundColor: 'transparent' }}>
                      +{extraCount} MORE
                    </span>
                  )}
                </div>
              );
            }

            return (
              <div 
                className="flex flex-col w-full h-full px-2 py-1 overflow-hidden rounded-none gap-0.5 relative"
                style={{ backgroundColor: `${statusColor}25`, borderLeft: `3px solid ${statusColor}` }}
                title={`${customerName} - ${serviceName} (${status}) ${extraCount > 0 ? `+ ${extraCount} more` : ''}`}
              >
                <div className="flex justify-between items-center w-full pr-8">
                  <span className="text-[10px] text-slate-700 font-bold leading-none shrink-0">{timeDisplay}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {status === 'invoiced' && (
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColor }} title="Invoiced" />
                    )}
                  </div>
                </div>
                <span className="font-bold text-[11px] text-slate-900 leading-none whitespace-normal break-words">{customerName}</span>
                <span className="text-[10px] text-slate-600 leading-none truncate">{serviceName}</span>
                {extraCount > 0 && (
                  <span className="absolute top-1 right-1 shrink-0 text-[var(--theme-primary)] text-[10px] font-bold uppercase tracking-wider leading-none drop-shadow-sm" style={{ backgroundColor: 'transparent' }}>
                    +{extraCount} MORE
                  </span>
                )}
              </div>
            );
          }}
          initialDate={activeDate}
          height="auto"
          contentHeight="auto"
        />
            </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {showMoreModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="absolute inset-0" onClick={() => { setShowMoreModal(false); setMoreModalDate(null); }}></div>
          <div className="relative bg-white shadow-2xl rounded-xl w-full max-w-lg flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <h3 className="text-[13px] font-bold text-slate-900 uppercase tracking-widest">
                Appointments ({moreModalDate && new Date(moreModalDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
              </h3>
              <button onClick={() => { setShowMoreModal(false); setMoreModalDate(null); }} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex flex-col gap-3">
              {(() => {
                const dayAppointments = aptList.filter(apt => {
                  const dateMatches = apt.date && apt.date.substring(0, 10) === moreModalDate;
                  const statusMatches = statusFilter === 'All' 
                    ? true 
                    : STATUS_GROUPS[statusFilter] 
                      ? STATUS_GROUPS[statusFilter].includes(apt.status) 
                      : apt.status === statusFilter;
                  return dateMatches && statusMatches;
                }).sort((a, b) => {
                  const dateA = new Date(`${a.date.substring(0, 10)}T${a.time.replace(/ AM| PM/, ':00')}`); 
                  const dateB = new Date(`${b.date.substring(0, 10)}T${b.time.replace(/ AM| PM/, ':00')}`);
                  return dateA - dateB;
                });
                
                if (dayAppointments.length === 0) return <p className="text-slate-500 text-sm">No appointments for this date.</p>;
                
                return dayAppointments.map(apt => {
                  const service = catalogServices.find(s => s.id === apt.serviceId);
                  const serviceName = service ? service.name : 'Unknown Service';
                  const statusColor = STATUS_COLORS[apt.status] || '#6366f1';

                  return (
                    <div 
                      key={apt.id} 
                      className="border border-slate-200 rounded-lg p-3 hover:border-[var(--theme-primary)] hover:shadow-sm cursor-pointer transition-all flex flex-col"
                      onClick={() => {
                        setShowMoreModal(false);
                        handleEventClick({ event: { id: apt.id } });
                      }}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-bold text-sm text-slate-900">{apt.customerName}</span>
                        <span className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: statusColor }}>
                          {apt.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-slate-500">
                        <span>{apt.time}</span>
                        <span className="font-medium text-slate-700">{serviceName}</span>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex justify-end bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="absolute inset-0" onClick={() => { setShowModal(false); setEditMode(false); }}></div>
          
          <div className="relative bg-white shadow-2xl h-full w-[450px] max-w-full flex flex-col animate-in slide-in-from-right duration-300">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{editMode ? 'Edit Booking' : 'New Booking'}</h3>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{editMode ? 'Update Appointment Details' : 'Schedule a new appointment'}</p>
              </div>
              <button onClick={() => { setShowModal(false); setEditMode(false); setErrors({}); }} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0">
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            <div className="flex-1 px-6 py-6 flex flex-col gap-5 overflow-y-auto custom-scrollbar bg-slate-50/30">
              <div className="grid grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Customer Name <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    value={newBooking.customerName}
                    onChange={e => {
                      setNewBooking({ ...newBooking, customerName: e.target.value });
                      if (errors.customerName) setErrors({...errors, customerName: undefined});
                    }}
                    placeholder="e.g. Priya Sharma"
                    className={`w-full h-10 bg-white border rounded-lg px-3 text-sm text-slate-900 font-medium focus:ring-1 focus:outline-none transition-all placeholder:text-slate-400 placeholder:font-normal shadow-sm ${errors.customerName ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500' : 'border-slate-200 focus:border-[var(--theme-primary)] focus:ring-[var(--theme-primary)]'}`}
                  />
                  {errors.customerName && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.customerName}</p>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Phone</label>
                  <input
                    type="tel"
                    value={newBooking.phone}
                    onChange={e => setNewBooking({ ...newBooking, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="9999999999"
                    className="w-full h-10 bg-white border border-slate-200 rounded-lg px-3 text-sm text-slate-900 font-medium focus:border-[var(--theme-primary)] focus:ring-1 focus:ring-[var(--theme-primary)] focus:outline-none transition-all placeholder:text-slate-400 placeholder:font-normal shadow-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5" style={{ zIndex: 102 }}>
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Service <span className="text-rose-500">*</span></label>
                  <CustomSelect
                    value={newBooking.serviceId}
                    onChange={val => {
                      const svc = myServices.find(s => s.id === val);
                      setNewBooking(prev => ({ 
                        ...prev, 
                        serviceId: val,
                        duration: svc ? svc.duration : prev.duration,
                        price: svc ? svc.price : prev.price
                      }));
                      if (errors.serviceId) setErrors({...errors, serviceId: undefined});
                    }}
                    options={myServices.map(s => ({ value: s.id, label: `${s.name} (${s.duration}m, ₹${s.price})` }))}
                    placeholder="Select a service"
                    className={`w-full text-sm font-medium h-10 shadow-sm ${errors.serviceId ? 'border border-rose-400' : ''}`}
                  />
                  {errors.serviceId && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.serviceId}</p>}
                </div>
                <div className="flex flex-col gap-1.5" style={{ zIndex: 101 }}>
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Staff</label>
                  <CustomSelect
                    value={newBooking.staffId}
                    onChange={val => setNewBooking({ ...newBooking, staffId: val })}
                    options={[
                      { value: '', label: '— Any Available —' },
                      ...myStaff.map(s => ({ value: s.id, label: s.name }))
                    ]}
                    className="w-full text-sm font-medium h-10 shadow-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5" style={{ zIndex: 100 }}>
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Date <span className="text-rose-500">*</span></label>
                  <CustomDatePickerFixed
                    value={newBooking.date}
                    onChange={val => {
                      if (val) {
                        setNewBooking({ ...newBooking, date: val });
                        if (errors.date) setErrors({...errors, date: undefined});
                      }
                    }}
                    className={`w-full text-sm font-medium h-10 shadow-sm ${errors.date ? 'border border-rose-400' : ''}`}
                  />
                  {errors.date && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.date}</p>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Duration (Mins)</label>
                  <input
                    type="number"
                    value={newBooking.duration}
                    onChange={e => setNewBooking({ ...newBooking, duration: e.target.value })}
                    className="w-full h-10 bg-white border border-slate-200 rounded-lg px-3 text-sm text-slate-900 font-medium focus:border-[var(--theme-primary)] focus:ring-1 focus:ring-[var(--theme-primary)] focus:outline-none transition-all shadow-sm"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2" style={{ zIndex: 99 }}>
                <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Time <span className="text-rose-500">*</span></label>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {TIME_SLOTS.map(slot => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => {
                        setNewBooking({ ...newBooking, time: slot });
                        if (errors.time) setErrors({...errors, time: undefined});
                      }}
                      className={`w-full px-1 py-2 rounded-lg text-[11px] font-bold transition-all shadow-sm border text-center ${newBooking.time === slot ? 'bg-[var(--theme-primary)] text-white border-transparent ring-2 ring-[var(--theme-primary)] ring-offset-1 scale-[1.02]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'} ${errors.time && newBooking.time !== slot ? 'border-rose-400 text-rose-600' : ''}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
                {errors.time && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.time}</p>}
              </div>

              <div className="grid grid-cols-3 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Price (₹)</label>
                  <input
                    type="number"
                    value={newBooking.price}
                    onChange={e => setNewBooking({ ...newBooking, price: e.target.value })}
                    className="w-full h-10 bg-white border border-slate-200 rounded-lg px-3 text-sm text-slate-900 font-medium focus:border-[var(--theme-primary)] transition-all shadow-sm"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Discount (₹)</label>
                  <input
                    type="number"
                    value={newBooking.discount}
                    onChange={e => setNewBooking({ ...newBooking, discount: e.target.value })}
                    className="w-full h-10 bg-white border border-slate-200 rounded-lg px-3 text-sm text-slate-900 font-medium focus:border-[var(--theme-primary)] transition-all shadow-sm"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Adv. Pay (₹)</label>
                  <input
                    type="number"
                    value={newBooking.advancePayment}
                    onChange={e => setNewBooking({ ...newBooking, advancePayment: e.target.value })}
                    className="w-full h-10 bg-white border border-slate-200 rounded-lg px-3 text-sm text-slate-900 font-medium focus:border-[var(--theme-primary)] transition-all shadow-sm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5" style={{ zIndex: 98 }}>
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Booking Status</label>
                  <CustomSelect
                    value={newBooking.status}
                    onChange={val => setNewBooking({ ...newBooking, status: val })}
                    options={[
                      { value: 'requested', label: 'Requested' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'confirmed', label: 'Confirmed' },
                      { value: 'checked-in', label: 'Checked In' }
                    ]}
                    className="w-full text-sm font-medium h-10 shadow-sm"
                  />
                </div>
                <div className="flex flex-col gap-1.5" style={{ zIndex: 97 }}>
                  <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Payment Status</label>
                  <CustomSelect
                    value={newBooking.paymentStatus}
                    onChange={val => setNewBooking({ ...newBooking, paymentStatus: val })}
                    options={[
                      { value: 'unpaid', label: 'Unpaid' },
                      { value: 'partial', label: 'Partially Paid' },
                      { value: 'paid', label: 'Paid' }
                    ]}
                    className="w-full text-sm font-medium h-10 shadow-sm"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Notes</label>
                <textarea
                  value={newBooking.notes}
                  onChange={e => setNewBooking({ ...newBooking, notes: e.target.value })}
                  placeholder="Optional notes..."
                  className="w-full h-16 bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-900 focus:border-[var(--theme-primary)] focus:ring-1 focus:ring-[var(--theme-primary)] focus:outline-none transition-all placeholder:text-slate-400 custom-scrollbar resize-none shadow-sm"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button onClick={() => { setShowModal(false); setEditMode(false); }} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200/60 rounded-lg transition-colors">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow-md transition-all disabled:opacity-60 flex items-center gap-2"
                style={{ background: accent }}
              >
                {saving ? 'Saving...' : (editMode ? 'Save Changes' : 'Book Appointment')}
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {selectedApt && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="absolute inset-0" onClick={() => setSelectedApt(null)}></div>
          
          <div className="relative bg-white shadow-2xl rounded-xl w-full max-w-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 border border-slate-100" style={{ maxHeight: '90vh' }}>
            <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between bg-white shrink-0">
              <div className="flex flex-col gap-1 min-w-0 pr-4">
                <h3 className="text-xl font-bold text-slate-900 leading-tight truncate uppercase">{selectedApt.customerName}</h3>
                <p className="text-[14px] font-medium text-slate-500 truncate flex items-center gap-2 uppercase">
                  <span className="font-semibold text-slate-700">Phone:</span> {selectedApt.phone || '-'}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => {
                    setEditMode(true);
                    setNewBooking({
                      id: selectedApt.id,
                      customerName: selectedApt.customerName || '',
                      phone: selectedApt.phone || '',
                      serviceId: selectedApt.serviceId || '',
                      staffId: selectedApt.staffId || '',
                      date: selectedApt.date || todayStr,
                      time: selectedApt.time || '10:00 AM',
                      duration: myServices.find(s => s.id === selectedApt.serviceId)?.duration || 30,
                      price: selectedApt.price || myServices.find(s => s.id === selectedApt.serviceId)?.price || 0,
                      discount: selectedApt.discount || 0,
                      advancePayment: selectedApt.advancePayment || 0,
                      status: selectedApt.status || 'pending',
                      paymentStatus: selectedApt.paymentStatus || 'unpaid',
                      notes: selectedApt.notes || ''
                    });
                    setShowModal(true);
                  }}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this appointment?')) {
                      deleteAppointment(selectedApt.id);
                      if (showNotification) showNotification('Appointment deleted', 'success');
                      setSelectedApt(null);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-rose-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all"
                >
                  <Trash2 size={12} /> Remove
                </button>
                <div className="w-px h-4 bg-slate-200 mx-2"></div>
                <button onClick={() => setSelectedApt(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0">
                  <X size={20} strokeWidth={2.5} />
                </button>
              </div>
            </div>
  
            <div className="px-6 py-6 flex flex-col gap-6 overflow-y-auto custom-scrollbar bg-slate-50/50">
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date</span>
                  <span className="text-[13px] font-semibold text-slate-800">{new Date(selectedApt.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Time</span>
                  <span className="text-[13px] font-semibold text-slate-800">{selectedApt.time}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Duration</span>
                  <span className="text-[13px] font-semibold text-slate-800">{selectedApt.duration || 30} Mins</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Booking Status</span>
                  <span className="text-[12px] font-bold uppercase" style={{ color: STATUS_COLORS[selectedApt.status] || accent }}>
                    {selectedApt.status}
                  </span>
                </div>
              </div>


              
              {selectedApt.notes && (
                <div className="flex flex-col gap-1.5 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Notes</span>
                  <p className="text-[13px] font-medium text-slate-700 whitespace-pre-wrap">{selectedApt.notes}</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2.5 whitespace-nowrap overflow-x-auto custom-scrollbar">
              <button onClick={() => setSelectedApt(null)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-slate-800 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all mr-auto">Close</button>

              {selectedApt.status !== 'cancelled' && selectedApt.status !== 'invoiced' && selectedApt.status !== 'completed' && selectedApt.status !== 'no-show' && selectedApt.status !== 'expired' && (
                <>
                  <button 
                    onClick={() => {
                      if (showNotification) showNotification('Please drag and drop the appointment on the calendar to reschedule', 'info');
                      setSelectedApt(null);
                    }} 
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-blue-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                  >
                    Reschedule
                  </button>
                  <button 
                    onClick={async () => {
                      try {
                        await updateAppointment(selectedApt.id, { status: 'no-show' });
                        if (showNotification) showNotification('Marked as No Show', 'info');
                      } catch (err) {
                        if (showNotification) showNotification('Failed to update status', 'error');
                      } finally {
                        setSelectedApt(null);
                      }
                    }} 
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-slate-800 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                  >
                    No Show
                  </button>
                  <button 
                    onClick={async () => {
                      try {
                        await updateAppointment(selectedApt.id, { status: 'cancelled' });
                        if (showNotification) showNotification('Appointment cancelled', 'info');
                      } catch (err) {
                        if (showNotification) showNotification('Failed to cancel', 'error');
                      } finally {
                        setSelectedApt(null);
                      }
                    }} 
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-rose-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                  >
                    Cancel
                  </button>
                </>
              )}

              {selectedApt.status === 'requested' && (
                <button 
                  onClick={async () => {
                    try {
                      await updateAppointment(selectedApt.id, { status: 'pending' });
                      if (showNotification) showNotification('Moved to Pending', 'success');
                    } catch (err) {
                      if (showNotification) showNotification('Failed to update status', 'error');
                    } finally {
                      setSelectedApt(null);
                    }
                  }} 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-purple-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                >
                  Accept Request
                </button>
              )}

              {(selectedApt.status === 'pending' || selectedApt.status === 'rescheduled') && (
                <button 
                  onClick={async () => {
                    try {
                      await updateAppointment(selectedApt.id, { status: 'confirmed' });
                      if (showNotification) showNotification('Appointment confirmed!', 'success');
                    } catch (err) {
                      if (showNotification) showNotification('Failed to confirm', 'error');
                    } finally {
                      setSelectedApt(null);
                    }
                  }} 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-amber-500 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                >
                  Confirm Booking
                </button>
              )}

              {selectedApt.status === 'confirmed' && (
                <button 
                  onClick={async () => {
                    try {
                      await updateAppointment(selectedApt.id, { status: 'checked-in' });
                      if (showNotification) showNotification('Customer Checked In', 'success');
                    } catch (err) {
                      if (showNotification) showNotification('Failed to check in', 'error');
                    } finally {
                      setSelectedApt(null);
                    }
                  }} 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-blue-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                >
                  Mark Checked-In
                </button>
              )}

              {selectedApt.status === 'checked-in' && (
                <button 
                  onClick={async () => {
                    try {
                      await updateAppointment(selectedApt.id, { status: 'started' });
                      if (showNotification) showNotification('Service Started', 'success');
                    } catch (err) {
                      if (showNotification) showNotification('Failed to start service', 'error');
                    } finally {
                      setSelectedApt(null);
                    }
                  }} 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-cyan-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                >
                  Start Service
                </button>
              )}
              
              {selectedApt.status === 'started' && (
                <button 
                  onClick={async () => {
                    try {
                      await updateAppointment(selectedApt.id, { status: 'completed' });
                      if (showNotification) showNotification('Appointment completed!', 'success');
                    } catch (err) {
                      if (showNotification) showNotification('Failed to mark completed', 'error');
                    } finally {
                      setSelectedApt(null);
                    }
                  }} 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-emerald-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                >
                  Mark Completed
                </button>
              )}

              {selectedApt.status === 'completed' && (
                <button 
                  onClick={async () => {
                    try {
                      await updateAppointment(selectedApt.id, { status: 'invoiced' });
                      if (showNotification) showNotification('Sent to Billing! Please complete the sale.', 'success');
                      if (onBillAppointment) {
                        const service = myServices.find(s => s.id === selectedApt.serviceId);
                        const staff = myStaff.find(s => s.id === selectedApt.staffId);
                        onBillAppointment({ apt: selectedApt, service, staff });
                      }
                    } catch (err) {
                      if (showNotification) showNotification('Failed to update status', 'error');
                    } finally {
                      setSelectedApt(null);
                    }
                  }} 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all shrink-0"
                >
                  Complete & Bill
                </button>
              )}
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
};

AppointmentsTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  onBillAppointment: PropTypes.func,
};
